import React, { useState } from 'react';
import '../InvoiceDetail.css';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch } from '../utils/api';

export default function InvoiceCreatePage() {
  const navigate = useNavigate();
  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState({
    numar_factura: '',
    data_emitere: today,
    data_scadenta: today,
    moneda: 'RON',
    modalitate_plata: 'ORDIN DE PLATA',
    seller_snapshot: { nume: '', cui: '', email: '', nr_reg_com: '', iban: '', adresa: '', oras: '', judet: '', tara: 'RO', tva: 21 },
    buyer_snapshot: { nume: '', cui: '', nr_reg_com: '', adresa: '', oras: '', judet: '', tara: 'RO' },
    linii_factura: [
      { 'BT-153': '', 'BT-129': 1, 'BT-130': 'H87', 'BT-146': 0, 'BT-152': 21, 'BT-151': 'S' }
    ],
  });
  const [saving, setSaving] = useState(false);
  const [createdId, setCreatedId] = useState(null);
  const [createdInvoice, setCreatedInvoice] = useState(null);
  const [message, setMessage] = useState(null);

  const updateField = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const updateParty = (party, field, value) => {
    setForm((f) => ({ ...f, [party]: { ...(f[party] || {}), [field]: value } }));
  };

  const updateSellerVat = (value) => {
    const vat = Number.isFinite(parseInt(value)) ? parseInt(value) : 21;
    setForm((f) => {
      // update seller_snapshot.tva
      const next = { ...f, seller_snapshot: { ...(f.seller_snapshot || {}), tva: vat } };
      // optionally autofill lines where VAT is blank/zero
      const lines = (next.linii_factura || []).map((ln) => {
        const cur = ln['BT-152'];
        if (cur === undefined || cur === null || cur === '' || Number(cur) === 0) {
          return { ...ln, 'BT-152': vat };
        }
        return ln;
      });
      next.linii_factura = lines;
      return next;
    });
  };

  const updateLine = (idx, field, value) => {
    setForm((f) => {
      const lines = f.linii_factura.slice();
      lines[idx] = { ...lines[idx], [field]: value };
      return { ...f, linii_factura: lines };
    });
  };

  const addLine = () => {
    setForm((f) => ({ ...f, linii_factura: [...f.linii_factura, { 'BT-153': '', 'BT-129': 1, 'BT-130': 'H87', 'BT-146': 0, 'BT-152': 21, 'BT-151': 'S' }] }));
  };

  const removeLine = (idx) => {
    setForm((f) => ({ ...f, linii_factura: f.linii_factura.filter((_, i) => i !== idx) }));
  };

  const validateInvoice = (invoiceData) => {
    const errors = {};

    // Validate seller
    if (!invoiceData.seller_snapshot?.nume?.trim()) {
      errors.seller_nume = 'Denumirea emitentului este obligatorie';
    }

    // Validate buyer
    if (!invoiceData.buyer_snapshot?.nume?.trim()) {
      errors.buyer_nume = 'Denumirea beneficiarului este obligatorie';
    }

    // Validate header
    if (!invoiceData.numar_factura?.trim()) {
      errors.numar_factura = 'Numărul facturii este obligatoriu';
    }
    if (!invoiceData.data_emitere) {
      errors.data_emitere = 'Data emiterii este obligatorie';
    }

    // Validate lines
    if (!invoiceData.linii_factura || invoiceData.linii_factura.length === 0) {
      errors.linii_factura = 'Cel puțin o linie de factură este obligatorie';
    } else {
      invoiceData.linii_factura.forEach((line, index) => {
        if (!line['BT-153']?.trim()) {
          errors[`line_${index}_description`] = `Denumirea produsului pe linia ${index + 1} este obligatorie`;
        }
        // EN 16931: cantitatea poate fi negativă (linii de discount); doar 0 este invalid
        const qty = parseFloat(line['BT-129']);
        if (isNaN(qty) || qty === 0) {
          errors[`line_${index}_quantity`] = `Cantitatea pe linia ${index + 1} trebuie să fie diferită de 0`;
        }
        if (!line['BT-146'] || parseFloat(line['BT-146']) <= 0) {
          errors[`line_${index}_price`] = `Prețul unitar pe linia ${index + 1} trebuie să fie mai mare ca 0`;
        }
      });
    }

    return errors;
  };

  const canApproveInvoice = (invoiceData) => {
    // Check 1: All required fields must be filled (no validation errors)
    const hasValidationErrors = Object.keys(validateInvoice(invoiceData || {})).length > 0;

    // Check 2: Invoice status must be GREEN
    const isGreen = invoiceData?.confidence_flag === 'GREEN';

    // Both conditions must be true to enable the button
    return !hasValidationErrors && isGreen;
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await apiFetch('/api/invoices', {
        method: 'POST',
        body: JSON.stringify(form)
      });
      if (!res.ok) {
        let msg = 'Eroare la salvarea facturii';
        try {
          const payload = await res.clone().json();
          if (payload?.errors) msg = payload.errors.join('; ');
          else if (payload?.message) msg = payload.message;
        } catch (_) {}
        throw new Error(msg);
      }
      const json = await res.json();
      setCreatedId(json.id);
      setCreatedInvoice(json);
      setMessage({ type: 'success', text: 'Factura a fost creată cu succes.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!createdId) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/invoices/${createdId}`, {
        method: 'PUT',
        body: JSON.stringify({ confidence_flag: 'GREEN', observatii_validare: 'Aprobată și transmisă în SPV ANAF', stare_trimitere: 'trimisa' })
      });
      if (!res.ok) {
        let msg = 'Eroare la trimiterea în ANAF';
        try { const payload = await res.clone().json(); if (payload?.message) msg = payload.message; } catch (_) {}
        throw new Error(msg);
      }
      // Navigate to the new invoice detail page
      navigate(`/invoice/${createdId}`);
    } catch (e) {
      setMessage({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="invoice-detail">
      <header className="invoice-detail-header">
        <div className="invoice-detail-header-content">
          <div className="invoice-detail-nav">
            <Link to="/validation-hub" className="back-btn">← Înapoi la Listă</Link>
            <div>
              <h1 className="invoice-detail-title">Creare Factură</h1>
              <p className="invoice-detail-subtitle">Introduceți datele și salvați, apoi trimiteți în SPV ANAF</p>
            </div>
          </div>
        </div>
      </header>

      <main className="invoice-detail-main">
        <div className="invoice-detail-content">
          {message && (
            <div className={`validation-message ${message.type === 'error' ? 'validation-message-error' : 'validation-message-success'}`}>
              <p className="validation-message-text">{message.text}</p>
            </div>
          )}

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Antet Factură</h2>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-field-label">Număr Factură *</label>
                  <input className="form-field-input" value={form.numar_factura} onChange={(e) => updateField('numar_factura', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Data Emiterii *</label>
                  <input type="date" className="form-field-input" value={form.data_emitere} onChange={(e) => updateField('data_emitere', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Data Scadenței</label>
                  <input type="date" className="form-field-input" value={form.data_scadenta} onChange={(e) => updateField('data_scadenta', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Moneda</label>
                  <input className="form-field-input" value={form.moneda} onChange={(e) => updateField('moneda', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Modalitate Plată</label>
                  <select
                    className="form-field-input"
                    value={form.modalitate_plata || 'ORDIN DE PLATA'}
                    onChange={(e) => updateField('modalitate_plata', e.target.value)}
                  >
                    <option value="nespecificata">Nespecificată</option>
                    <option value="NUMERAR">NUMERAR</option>
                    <option value="ORDIN DE PLATA">ORDIN DE PLATĂ</option>
                    <option value="CARD">CARD</option>
                    <option value="RAMBURS">RAMBURS</option>
                  </select>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Emitent</h2>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-field-label">Denumire *</label>
                  <input className="form-field-input" value={form.seller_snapshot.nume} onChange={(e) => updateParty('seller_snapshot', 'nume', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">CUI *</label>
                  <input className="form-field-input form-field-input-mono" value={form.seller_snapshot.cui} onChange={(e) => updateParty('seller_snapshot', 'cui', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Email</label>
                  <input type="email" className="form-field-input" value={form.seller_snapshot.email} onChange={(e) => updateParty('seller_snapshot', 'email', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Nr. Reg. Com.</label>
                  <input className="form-field-input" value={form.seller_snapshot.nr_reg_com} onChange={(e) => updateParty('seller_snapshot', 'nr_reg_com', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">IBAN</label>
                  <input
                    className="form-field-input form-field-input-mono"
                    value={form.seller_snapshot.iban}
                    onChange={(e) => updateParty('seller_snapshot', 'iban', e.target.value)}
                    placeholder="RO00XXXX0000000000000000"
                  />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Adresă</label>
                  <input className="form-field-input" value={form.seller_snapshot.adresa} onChange={(e) => updateParty('seller_snapshot', 'adresa', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Oraș</label>
                  <input className="form-field-input" value={form.seller_snapshot.oras} onChange={(e) => updateParty('seller_snapshot', 'oras', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Județ</label>
                  <input className="form-field-input" value={form.seller_snapshot.judet} onChange={(e) => updateParty('seller_snapshot', 'judet', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Țara</label>
                  <input className="form-field-input" value={form.seller_snapshot.tara} onChange={(e) => updateParty('seller_snapshot', 'tara', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">TVA implicit (%)</label>
                  <input type="number" min="0" max="100" className="form-field-input" value={form.seller_snapshot.tva ?? 21} onChange={(e) => updateSellerVat(e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Beneficiar</h2>
            </div>
            <div className="section-content">
              <div className="form-grid">
                <div className="form-field">
                  <label className="form-field-label">Denumire *</label>
                  <input className="form-field-input" value={form.buyer_snapshot.nume} onChange={(e) => updateParty('buyer_snapshot', 'nume', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">CUI *</label>
                  <input className="form-field-input form-field-input-mono" value={form.buyer_snapshot.cui} onChange={(e) => updateParty('buyer_snapshot', 'cui', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Nr. Reg. Com.</label>
                  <input className="form-field-input" value={form.buyer_snapshot.nr_reg_com} onChange={(e) => updateParty('buyer_snapshot', 'nr_reg_com', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Adresă</label>
                  <input className="form-field-input" value={form.buyer_snapshot.adresa} onChange={(e) => updateParty('buyer_snapshot', 'adresa', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Oraș</label>
                  <input className="form-field-input" value={form.buyer_snapshot.oras} onChange={(e) => updateParty('buyer_snapshot', 'oras', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Județ</label>
                  <input className="form-field-input" value={form.buyer_snapshot.judet} onChange={(e) => updateParty('buyer_snapshot', 'judet', e.target.value)} />
                </div>
                <div className="form-field">
                  <label className="form-field-label">Țara</label>
                  <input className="form-field-input" value={form.buyer_snapshot.tara} onChange={(e) => updateParty('buyer_snapshot', 'tara', e.target.value)} />
                </div>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-header">
              <h2 className="section-title">Linii de Factură</h2>
              <button className="review-btn" onClick={addLine}>Adaugă linie</button>
            </div>
            <div className="section-content">
              <div className="lines-table-container">
                <table className="lines-table">
                  <thead className="lines-table-head">
                    <tr>
                      <th>Denumire</th>
                      <th className="text-right">Cantitate</th>
                      <th>U.M.</th>
                      <th className="text-right">Preț Unitar</th>
                      <th className="text-center">TVA %</th>
                      <th>Categorie TVA</th>
                      <th>Acțiuni</th>
                    </tr>
                  </thead>
                  <tbody className="lines-table-body">
                    {form.linii_factura.map((line, idx) => (
                      <tr key={idx}>
                        <td>
                          <textarea className="lines-table-textarea" value={line['BT-153'] || ''} onChange={(e) => updateLine(idx, 'BT-153', e.target.value)} />
                        </td>
                        <td>
                          <input type="number" step="0.01" className="lines-table-input lines-table-input-mono" value={line['BT-129'] || 0} onChange={(e) => updateLine(idx, 'BT-129', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td>
                          <input className="lines-table-input" value={line['BT-130'] || 'H87'} onChange={(e) => updateLine(idx, 'BT-130', e.target.value)} placeholder="BUC" />
                        </td>
                        <td>
                          <input type="number" step="0.0001" className="lines-table-input lines-table-input-mono" value={line['BT-146'] || 0} onChange={(e) => updateLine(idx, 'BT-146', parseFloat(e.target.value) || 0)} />
                        </td>
                        <td>
                <input type="number" min="0" max="100" className="lines-table-input" value={line['BT-152'] ?? 0} onChange={(e) => updateLine(idx, 'BT-152', Number.isNaN(parseInt(e.target.value, 10)) ? 0 : parseInt(e.target.value, 10))} />
                        </td>
                        <td>
                          <select className="lines-table-input" value={line['BT-151'] || 'S'} onChange={(e) => updateLine(idx, 'BT-151', e.target.value)}>
                            <option value="S">Standard</option>
                            <option value="O">Outside VAT</option>
                            <option value="E">Exempt</option>
                            <option value="AE">Reverse Charge</option>
                          </select>
                        </td>
                        <td>
                          <button className="review-btn" onClick={() => removeLine(idx)}>Șterge</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="section-card">
            <div className="section-content">
              <div className="actions-container">
                <div className="actions-info">
                  <h3>Acțiuni</h3>
                  <p>Salvați factura înainte de trimitere în SPV ANAF</p>
                </div>
                <div className="actions-buttons">
                  <button type="button" onClick={handleSave} disabled={saving} className={`action-btn ${saving ? 'action-btn-processing' : 'action-btn-save'}`}>
                    {saving ? 'Se salvează...' : 'Salvează'}
                  </button>
                  <button type="button" onClick={handleSend} disabled={!createdId || saving || !canApproveInvoice(createdInvoice)} className={`action-btn action-btn-approve ${!createdId || saving || !canApproveInvoice(createdInvoice) ? 'action-btn-disabled' : ''}`}>
                    Trimite ANAF
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
